import React, { useEffect, useImperativeHandle, useRef, useState } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from 'primereact/button';
import { Divider } from 'primereact/divider';
import { InputText } from 'primereact/inputtext';
import { Toast } from 'primereact/toast';
import { SubmitHandler, useForm } from 'react-hook-form';

import ServiceResult from '../components/services/interfaces/ServiceResult';
import { TaskClosingCodeSchema } from '../constants/schemas';
import useClosingCodesService from '../hooks/services/useClosingCodesService';
import { TaskClosingCode } from '../types/interfaces';
import { TaskClosingCodeProps } from '../types/props';

import ConfirmationDialog from './shared/ConfirmationDialog';
import DeleteConfirmationDialog from './shared/DeleteConfirmationDialog';

interface RefInterface {
  handleCancel: () => void;
}

const TaskClosingCodeCard = React.forwardRef<
  RefInterface,
  TaskClosingCodeProps
>(
  (
    {
      taskClosingCodeProps,
      onAdd,
      onUpdate,
      onDelete,
      onRestore,
      closeDialog
    }: TaskClosingCodeProps,
    ref: React.Ref<RefInterface>
  ) => {
    const {
      register,
      handleSubmit,
      watch,
      reset,
      setError,
      formState: { isDirty, errors }
    } = useForm({
      resolver: zodResolver(TaskClosingCodeSchema),
      defaultValues: taskClosingCodeProps
    });
    const [showDialog, setShowDialog] = useState(false);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [closingCodeErrors, setClosingCodeErrors] = useState<string>('');

    const toast = useRef<Toast>(null);
    const taskClosingCodeId = watch('id');
    const taskClosingCode = watch('taskClosingCode');
    const isDeleted = watch('isDeleted'); // Watch the isDeleted flag

    const closingCodesService = useClosingCodesService();
    useEffect(() => {
      let serviceResult: ServiceResult<TaskClosingCode | undefined>;
      if (taskClosingCodeProps.id) {
        serviceResult = closingCodesService.get(
          parseInt(taskClosingCodeProps.id.toString())
        );
        serviceResult.result.then((r) => {
          reset(r);
        });
      }

      return () => {
        if (serviceResult) serviceResult.cancel();
      };
    }, [closingCodesService, reset, taskClosingCodeProps.id]);

    const onSubmit: SubmitHandler<TaskClosingCode> = async (data) => {
      setClosingCodeErrors('');
      const tabError = 'Запись с таким названием уже есть в справочнике';
      const newData = { ...data };
      const dataId = data.id;
      let result: number | string | undefined = 0;
      const isIdExist =
        data && dataId !== undefined && dataId !== null && dataId !== '';
      if (onUpdate && isIdExist) {
        result = await onUpdate(dataId, newData);
      }
      if (onAdd && !isIdExist) {
        newData.id = 0;
        result = await onAdd(newData);
      }
      if (result === 'TaskClosingCode Not Unique') {
        setClosingCodeErrors(tabError);
        setError('taskClosingCode', {
          type: 'value',
          message: 'Пожалуйста, укажите уникальное название'
        });
      } else if (result === 'ShortName Not Unique') {
        setClosingCodeErrors(tabError);
        setError('shortName', {
          type: 'value',
          message: 'Пожалуйста, укажите уникальное название'
        });
      } else {
        closeDialog && closeDialog();
      }
    };

    const handleCancel = () => {
      if (isDirty) {
        setShowDialog(true);
      }
      if (closeDialog && !isDirty) {
        closeDialog();
      }
    };

    useImperativeHandle(ref, () => {
      return {
        handleCancel
      };
    });

    const handleDelete = () => {
      setShowDeleteDialog(true);
    };

    const handleRestoreClick = () => {
      if (onRestore) {
        onRestore(Number(taskClosingCodeId));
      }
      closeDialog && closeDialog();
    };

    const handleConfirmationContinue = () => {
      setShowDialog(false);
      if (closeDialog) {
        closeDialog();
      }
      // Perform navigation/close the card without saving
    };

    const handleDeleteCancellation = () => {
      setShowDeleteDialog(false);
    };

    const handleDeleteConfirmation = () => {
      if (onDelete) {
        onDelete(taskClosingCodeId);
        closeDialog && closeDialog();
      }
      setShowDeleteDialog(false);
    };

    const handleConfirmationCancel = () => {
      setShowDialog(false);
    };

    return (
      <div className="dialog-tabless-card dialog-tabless-card--fixed-footer closing-code-card__roles">
        <Toast ref={toast} />
        <div className=" gap-3 pb-0">
          <Divider />
          {closingCodeErrors && (
            <p
              style={{ height: '14px', width: '100%', fontSize: '12px' }}
              className={`closing-code-card-errors card-errors ${
                closingCodeErrors ? 'text-red-500' : 'text-transparent'
              } `}
            >
              {closingCodeErrors ?? ' '}
            </p>
          )}
          <div className="taskClosingCode-card__column">
            <div className="flex flex-column">
              <div className=" w-full">
                <div className="flex flex flex-row justify-content-between align-items-center gap-3 w-full">
                  <label htmlFor="taskClosingCode" className="col p-0">
                    Полное название<span className="required">*</span>:
                  </label>
                  <div className="col-8 p-0">
                    <InputText
                      className="user-card__input w-full"
                      {...register('taskClosingCode')}
                      disabled={isDeleted}
                    />
                  </div>
                </div>
                <p
                  className={`taskClosingCode-card-errors card-errors ${
                    errors.taskClosingCode ? 'text-red-500' : 'text-transparent'
                  } `}
                >
                  {errors.taskClosingCode
                    ? errors.taskClosingCode.message
                    : ' '}
                </p>
              </div>

              <div className=" w-full">
                <div className="flex flex flex-row justify-content-between align-items-center gap-3 w-full">
                  <label htmlFor="shortName" className="col p-0">
                    Короткое название<span className="required">*</span>:
                  </label>
                  <div className="col-8  p-0">
                    <InputText
                      className="user-card__input w-full"
                      {...register('shortName')}
                      disabled={isDeleted}
                    />
                  </div>
                </div>
                <p
                  className={`taskClosingCode-card-errors card-errors ${
                    errors.shortName ? 'text-red-500' : 'text-transparent'
                  } `}
                >
                  {errors.shortName ? errors.shortName.message : ' '}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="default-card__buttons">
          <div className="flex gap-3">
            {!isDeleted && (
              <Button
                label="Сохранить"
                icon="pi pi-check"
                onClick={handleSubmit(onSubmit)}
              />
            )}
            {isDeleted && (
              <Button
                label="Восстановить"
                icon="pi pi-refresh"
                onClick={handleRestoreClick}
              />
            )}
            {!isDeleted && (
              <Button
                label="Удалить"
                icon="pi pi-trash"
                onClick={handleDelete}
                className="p-button-danger"
              />
            )}
            <Button
              label="Отмена"
              icon="pi pi-times"
              onClick={handleCancel}
              className="p-button-secondary"
            />
          </div>
        </div>

        <ConfirmationDialog
          visible={showDialog}
          onCancel={handleConfirmationCancel}
          onContinue={handleConfirmationContinue}
        />

        <DeleteConfirmationDialog
          visible={showDeleteDialog}
          onHide={handleDeleteCancellation}
          onConfirm={handleDeleteConfirmation}
          entityType="вид шифр закрытия наряда"
          entityName={taskClosingCode}
        />
      </div>
    );
  }
);

TaskClosingCodeCard.displayName = 'TaskClosingCodeCard';

export default TaskClosingCodeCard;
